// Analytics for the two hands-on flows: the payouts simulator and the business onboarding
// (welcome) flow. FullStory session capture and Segment events both start when one of those
// screens mounts — the rest of the portal (docs, reference) is never recorded or tracked.
//
// Event properties must never carry an API key, tax id, owner email or any other value typed
// into the onboarding forms.
import { FullStory, init, isInitialized } from '@fullstory/browser';
import { Analytics } from '@melio/platform-analytics';
import { useEffect } from 'react';

import { config } from '@/config';

type Properties = Record<string, unknown>;

export type TrackedFlow = 'simulator' | 'welcome' | 'onboarding';

let analyticsStarted = false;

function start() {
  if (!analyticsStarted) {
    Analytics.init(config.analytics);
    analyticsStarted = true;
  }
  if (config.fullStoryOrgId && !isInitialized()) {
    init({ orgId: config.fullStoryOrgId, devMode: config.environment === 'development' });
  }
}

export function trackEvent(flow: TrackedFlow, action: string, properties: Properties = {}) {
  const enriched = { environment: config.environment, ...properties };
  Analytics.getInstance().track(flow, action, enriched);
  if (isInitialized()) {
    FullStory('trackEvent', { name: `${flow} ${action}`, properties: enriched });
  }
}

// Object-Action event pair for the partner-onboarding wizard: one `Partner-View` per step shown, one
// `Partner-Click` per button pressed. `Analytics.track(viewContext, action, ...)` names the underlying
// Segment event `${viewContext}-${action}`, so passing viewContext 'Partner' yields exactly
// 'Partner-View' / 'Partner-Click' — no separate naming logic needed.
interface PartnerEventBase {
  userId?: string;
  userEmail?: string;
  /** kebab-case page identifier, derived from the step's on-screen title. */
  pageName: string;
  /** The step's business intent, supplied by the caller per step. */
  intent: string;
}

export interface PartnerClickProps extends PartnerEventBase {
  /** The clicked button's name, e.g. 'continue', 'back', 'submit'. */
  cta: string;
  /** Extra step-specific data (e.g. the chosen integration type, or a form value). */
  values?: Properties;
}

function partnerEventProperties(base: PartnerEventBase, extra?: Properties): Properties {
  return {
    UserId: base.userId,
    UserEmail: base.userEmail,
    PageName: base.pageName,
    Flow: 'partner-onboarding',
    PathName: window.location.pathname,
    Intent: base.intent,
    ...extra,
  };
}

export function trackPartnerView(props: PartnerEventBase) {
  const properties = partnerEventProperties(props);
  Analytics.getInstance().track('Partner', 'View', properties);
  if (isInitialized()) {
    FullStory('trackEvent', { name: 'Partner-View', properties });
  }
}

export function trackPartnerClick({ cta, values, ...base }: PartnerClickProps) {
  const properties = partnerEventProperties(base, { Cta: cta, ...(values ? { Values: values } : {}) });
  Analytics.getInstance().track('Partner', 'Click', properties);
  if (isInitialized()) {
    FullStory('trackEvent', { name: 'Partner-Click', properties });
  }
}

// Boots analytics for the flows we track and tags the session with the flow it started on. By
// default this also fires a `${flow}-Viewed` event the first time (setViewContext's own behavior,
// with no viewFn given) — skip it for a flow that already tracks its own view events (e.g. onboarding's
// Partner-View), so the vendor's default doesn't add a redundant one alongside them.
export function useFlowAnalytics(flow: TrackedFlow, options: { skipViewEvent?: boolean } = {}) {
  useEffect(() => {
    start();
    Analytics.getInstance().setViewContext(flow, options.skipViewEvent ? () => {} : undefined);
    if (isInitialized()) {
      FullStory('setProperties', { type: 'page', properties: { pageName: flow, environment: config.environment } });
    }
  }, [flow]);
}
